import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { imgBaseUrl } from 'configs';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Switch,
  Select
} from '@material-ui/core';
import JoditEditor from 'jodit-react';
import NumberFormat from 'react-number-format';
import { singleUnitName } from 'helpers/other';
import {
  addTasksToQueueApi
} from 'api';
import logo from 'components/Logo/logo-dermpro.svg';
import { updateEditableQuoteProduct } from 'redux/actions/quote';
import { extractAttributesValues } from 'pages/quotes/helpers';
import { unitConventions } from './DiscountOptions/helpers/quoteDiscounts';
import QuoteProductInfoTable from './QuoteProductInfoTable';
import useStyles from './styles';


const config = {
  'showXPathInStatusbar': false,
  'spellcheck': false,
  'askBeforePasteHTML': false,
  'askBeforePasteFromWord': false,
  'disablePlugins': 'table,preview,table-keyboard-navigation,image,media,video,file,add-new-line,about,class-span,focus,delete,wrap-text-nodes,copy-format,clipboard,color,drag-and-drop,drag-and-drop-element,error-messages,font,format-block,fullsize,hotkeys,iframe,indent,hr,inline-popup,justify,limit,link,mobile,ordered-list,placeholder,redo-undo,resizer,search,select,size,resize-handler,source,stat,sticky,symbols,tooltip,xpath,image-properties,image-processor',
  'buttons': 'bold,italic,underline,ul,ol'
};

const attributesToFind = [
  'short_description',
  'image',
  'catalog_product_unit',
  'savings',
  'loyalty_system',
  'quote_frequency'
];

const QuoteProductDetails = ({editableQuoteProduct}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const {
    sku,
    price,
    status,
    name,
    type_id,
    custom_attributes,
    extension_attributes,
  } = editableQuoteProduct;

  const {
    image,
    catalog_product_unit,
    loyalty_system,
    savings,
    short_description,
    quote_frequency,
  } = extractAttributesValues(custom_attributes, attributesToFind);

  const { profile: { stores } } = useSelector((state: IReduxState) => state.user);
  const [{
    apiUrl,
    quoteProStoreCode
  }] = stores;

  const isProduct = sku?.includes('PROD');

  const mediaStoreUrl = `${apiUrl}/media/catalog/product` || imgBaseUrl;
  const imageSource = image
    ? `${mediaStoreUrl}${image}`
    : logo;

  const existingQuoteproDiscounts = editableQuoteProduct?.custom_attributes?.find((attr) => attr.attribute_code === 'quotepro_discounts');

  const fixedDiscountValues = existingQuoteproDiscounts?.value ? JSON.parse(existingQuoteproDiscounts.value).flatMap((v) => v.price_type === 'fixed' ? Math.abs(v.price) : []) : [0];
  const maxDiscount = Math.max(...fixedDiscountValues);
  const minProductPrice = +editableQuoteProduct?.custom_attributes?.find((item) => item.attribute_code === 'catalog_min_price')?.value;

  const [stockItemChanged, setStockItemChanged] = useState(false);

  const magentoImgUrlRegex = new RegExp(/url\s*=\s*"(.+?)"/);
  const magentoImgSrcRegex = new RegExp(/src\s*=\s*"(.+?)}}"/);
  const descriptionImgSrcRegex = new RegExp(/src\s*=\s*"(.+?)"/);
  const pathForUrlRegex = new RegExp(/medias*\s*(.+?)"/);

  const replaceCustomAttributeByAttrCode = (original, changed) => {
    const newAttributes = changed.filter((attr) => !original.find((a) => a.attribute_code === attr.attribute_code));
    const originalAttributes = original.map((obj) => changed.find((o) => o.attribute_code === obj.attribute_code) || obj);

    return [...originalAttributes, ...newAttributes];
  };

  const parseProductDescription = (description) => {
    const matchUrl = magentoImgUrlRegex.exec(description);
    const matchImgSrc = magentoImgSrcRegex.exec(description);
    const defaultSrcForImg = descriptionImgSrcRegex.exec(description);
    const imgSrc = matchUrl ? `src="${stores[0]?.storeUrl}/media/${matchUrl[1]}"` : defaultSrcForImg[0];

    return matchImgSrc ? description.replace(matchImgSrc[0], imgSrc) : description.replace(imgSrc, imgSrc);
  };

  let validatedShortDescription = short_description?.value || '';

  if (validatedShortDescription?.includes('img')) {
    validatedShortDescription = parseProductDescription(short_description);
  }

  const editProductDescription = (newDescription, attributeCode, setFieldValue) => {
    validatedShortDescription = newDescription.replace(/style="(.*?)"/gm, '');

    if (validatedShortDescription.includes('img')) {
      const matchNewSrc = descriptionImgSrcRegex.exec(validatedShortDescription);
      const matchNewPath = pathForUrlRegex.exec(validatedShortDescription);
      const srcForStoreImg = matchNewPath ? `src="{{media url="${matchNewPath[1]}"}}"` : matchNewSrc[0];
      validatedShortDescription = validatedShortDescription.replace(matchNewSrc[0], srcForStoreImg);
    }

    setFieldValue(attributeCode, validatedShortDescription);
  };

  const editProductQty = (qty, editableProductValues) => {
    setStockItemChanged(true);
    const {
      price,
      status,
      name,
      custom_attributes
    } = editableProductValues;
    let stock_item = {...extension_attributes.stock_item, is_in_stock: true, qty };

    if (!qty) {
      stock_item = { ...stock_item, is_in_stock: false, qty: 0 };
    }

    const product = {
      ...editableQuoteProduct,
      price,
      status,
      name,
      custom_attributes: replaceCustomAttributeByAttrCode(editableQuoteProduct.custom_attributes, custom_attributes),
      extension_attributes: {
        ...editableQuoteProduct.extension_attributes,
        stock_item
      }
    };

    dispatch(updateEditableQuoteProduct(product));
  };

  const handleChangeStock = (checked, editableProductValues) => {
    setStockItemChanged(true);
    const {
      price,
      status,
      name,
      custom_attributes,
    } = editableProductValues;

    let stock_item = {...extension_attributes.stock_item, is_in_stock: false, qty: 0 };

    if (checked) {
      stock_item = {...extension_attributes.stock_item, is_in_stock: true, qty: 999};
    }

    const product = {
      ...editableQuoteProduct,
      price,
      status,
      name,
      custom_attributes: replaceCustomAttributeByAttrCode(editableQuoteProduct.custom_attributes, custom_attributes),
      extension_attributes: {
        ...editableQuoteProduct.extension_attributes,
        stock_item
      }
    };

    dispatch(updateEditableQuoteProduct(product));
  };

  const handleChangePrice = (price, editableProductValues) => {
    const {
      status,
      name,
      custom_attributes,
    } = editableProductValues;

    const product = {
      ...editableQuoteProduct,
      price,
      status,
      name,
      custom_attributes: replaceCustomAttributeByAttrCode(editableQuoteProduct.custom_attributes, custom_attributes)
    };

    dispatch(updateEditableQuoteProduct(product));
  };

  const handleSaveProduct = ({price, status, name, custom_attributes, qty, inStock}) => {
    const requiredAttributes = editableQuoteProduct.custom_attributes
      .filter((attr) => (
        attr.attribute_code === 'catalog_quotepro_groups' ||
        attr.attribute_code === 'image'
      ));
    const task = {
      body: {
        price,
        status,
        name,
        custom_attributes: [...custom_attributes, ...requiredAttributes],
        extension_attributes: stockItemChanged ? {
          stock_item: {
            qty,
            is_in_stock: inStock,
          }
        } : {},
      },
      name,
      sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: quoteProStoreCode
    };

    addTasksToQueueApi({dispatch, tasks: [task]});
    history.goBack();
  };

  return (
    <Card className="mx-10">
      {editableQuoteProduct &&
        <Formik
          initialValues={{
            price,
            status,
            name,
            custom_attributes: [
              {attribute_code: 'catalog_product_unit', value: catalog_product_unit || 'units'},
              {attribute_code: 'loyalty_system', value: loyalty_system || ''},
              {attribute_code: 'savings', value: savings || ''},
              {attribute_code: 'short_description', value: short_description || ''},
              {attribute_code: 'quote_frequency', value: quote_frequency || ''}
            ],
            qty: extension_attributes.stock_item.qty,
            inStock: !!extension_attributes.stock_item.is_in_stock,
          }}
          onSubmit={handleSaveProduct}
          enableReinitialize
          validationSchema={
            yup.object({
              name: yup
                .string()
                .required('Field is required'),
              price: yup
                .number()
                .min(0, 'Price should be positive number')
                .min(minProductPrice || 0, `MAP cannot go lower than $${minProductPrice || 0}`)
                .test(
                  'price',
                  `The price can't be lower max discount $${maxDiscount.toFixed(2)}`,
                  (val) => {
                    if (!val) {
                      return true;
                    }

                    return maxDiscount <= val;
                  }
                )
                .required('Field is required'),
              custom_attributes: yup.array().of(
                yup.object().shape({
                  attribute_code: yup.string(),
                  value: yup.mixed().when('attribute_code', {
                    is: 'savings',
                    then: yup.number().min(0, 'Savings should be positive number')
                  })
                })
              )
            })
          }
        >
          {({
            values: {
              price,
              status,
              name,
              custom_attributes,
              qty,
              inStock,
            },
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            handleSubmit
          }) => (
            <form onSubmit={handleSubmit}>
              <CardHeader
                className={classes.cardHeader}
                title="Listing Details"
                action={
                  <>
                    <Button
                      className="mr-5"
                      onClick={() => history.goBack()}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                    >
                      Save Changes
                    </Button>
                  </>
                }
              />
              <CardContent className={classes.CardContent}>
                <Grid container spacing={5} className="mt-5">
                  <Grid item xs={12} md={4}>
                    <img
                      src={imageSource}
                      alt={name}
                      className={classes.productImage}
                      onError={(e) => {
                        (e.target as HTMLImageElement).onerror = null;
                        (e.target as HTMLImageElement).src = logo;
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          Item Name
                          <TextField
                            fullWidth
                            variant="outlined"
                            className="my-2"
                            inputProps={{
                              className: 'font-bold'
                            }}
                            name="name"
                            value={name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} className="mr-2">
                        <QuoteProductInfoTable sku={sku} product={editableQuoteProduct} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          <div>Enable Item</div>
                          <Switch
                            color="primary"
                            name="status"
                            title="Click here to hide an item from your store"
                            checked={status == 1}
                            onChange={({target: {checked}}) => setFieldValue('status', checked ? 1 : 2)}
                          />
                        </Typography>
                      </Grid>
                      {isProduct &&
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body1" className="py-4 flex items-center">
                            Quantity:
                            <NumberFormat
                              allowNegative={false}
                              name="qty"
                              value={qty}
                              className="mx-2"
                              inputProps={{className: 'text-center min-w-100' }}
                              onValueChange={(val) => {
                                setFieldValue('qty', val.floatValue);
                                editProductQty(val.floatValue, values);
                              }}
                              onBlur={handleBlur}
                              customInput={TextField}
                            />
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body1" className="pr-2 font-bold">
                            <div>In Stock</div>
                            <Switch
                              onChange={({target: {checked}}) => {
                                setFieldValue('inStock', checked);
                                handleChangeStock(checked, values);
                              }}
                              checked={inStock}
                              color="primary"
                              name="is_in_stock"
                              inputProps={{ 'aria-label': 'primary checkbox' }}
                              className="ml-2"
                            />
                          </Typography>
                        </Grid>
                      </>
                      }
                      {/* <Grid item md={6} xs={12}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          Loyalty program
                          <Select
                            fullWidth
                            displayEmpty
                            variant="outlined"
                            className="mt-2"
                            name="custom_attributes[1].value"
                            value={custom_attributes[1].value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value="">N/A</MenuItem>
                            <MenuItem value="alle">Allē</MenuItem>
                            <MenuItem value="aspire">Aspire</MenuItem>
                            <MenuItem value="opus">Opus</MenuItem>
                          </Select>
                        </Typography>
                      </Grid> */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          Price
                          <NumberFormat
                            prefix="$"
                            fixedDecimalScale
                            decimalScale={2}
                            thousandSeparator
                            fullWidth
                            variant="outlined"
                            className="mt-2"
                            name="price"
                            value={price}
                            disabled={type_id === 'configurable'}
                            onValueChange={(val) => {
                              setFieldValue('price', val.floatValue);
                              handleChangePrice(val.floatValue, values);
                            }}
                            onBlur={handleBlur}
                            error={touched.price && Boolean(errors.price)}
                            helperText={touched.price && errors.price}
                            customInput={TextField}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          Savings
                          <NumberFormat
                            prefix="$"
                            fixedDecimalScale
                            decimalScale={2}
                            thousandSeparator
                            fullWidth
                            variant="outlined"
                            className="mt-2"
                            name="custom_attributes[2].value"
                            value={custom_attributes[2].value}
                            onValueChange={(val) => setFieldValue('custom_attributes[2].value', val.floatValue || '')}
                            onBlur={handleBlur}
                            error={touched.custom_attributes?.[2]?.value && Boolean(errors.custom_attributes?.[2]?.value)}
                            helperText={touched.custom_attributes?.[2]?.value && errors.custom_attributes?.[2]?.value}
                            customInput={TextField}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body1" className="pr-2 font-bold">
                          Name of unit convention
                          <Select
                            fullWidth
                            variant="outlined"
                            className="mt-2"
                            name="custom_attributes[0].value"
                            value={custom_attributes[0].value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            {unitConventions.map((item) => (
                              <MenuItem value={item.value} key={item.value}>{item.name}</MenuItem>
                            ))}
                          </Select>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body1" className="pr-2 font-bold mb-2">
                          Frequency
                        </Typography>
                        <NumberFormat
                          suffix={` ${custom_attributes[4].value == 1
                            ? singleUnitName(custom_attributes[0].value)
                            : custom_attributes[0].value} per year`
                          }
                          fixedDecimalScale
                          decimalScale={0}
                          thousandSeparator
                          allowNegative={false}
                          variant="outlined"
                          className="w-full"
                          name="custom_attributes[4].value"
                          value={custom_attributes[4].value}
                          disabled={type_id === 'configurable'}
                          onValueChange={(val) => {
                            setFieldValue(
                              'custom_attributes[4].value',
                              !val.floatValue ? '' : val.floatValue
                            );
                          }}
                          onBlur={handleBlur}
                          customInput={TextField}
                        />
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Typography variant="body1" className=" pr-2 font-bold">Item Short Description</Typography>
                        <JoditEditor
                          value={custom_attributes[3].value}
                          config={config}
                          onChange={handleChange}
                          onBlur={(newContent) => editProductDescription(newContent, 'custom_attributes[3].value', setFieldValue)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </form>
          )}
        </Formik>
      }
    </Card>
  );
};

export default QuoteProductDetails;
